// /brands — Brand chooser / manager. Deliberately rendered OUTSIDE the
// AppLayoutV2 brand-scoped shell with its own minimal chrome (logo + back-
// to-app link). Reasoning: every brand-scoped surface keeps in-memory state
// keyed to the active brand. Switching mid-flow leaves stale React state,
// in-flight API requests, and cached generated images everywhere. Pulling
// /brands out of the shell + hard-reloading on activate is the only
// deterministic way to flush all of that.
//
// useBrand.switchBrand and removeBrand both window.location.assign().
// addBrand stays a pure CRUD op — the caller chains switchBrand(newId,
// '/brand') to drop the user into the new brand's setup.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '@/hooks/useBrand';
import { useCredits } from '@/hooks/useCredits';
import { useConfirm } from '@/hooks/useConfirm';
import type { PlanTier } from '@/hooks/useCredits';
import { brandAvatarStyle } from '@/lib/brandThumbnail';
import { Logo } from '@/components/feature/Logo';

// Mirrors BRAND_CAPS in backend/helpers/brand.py. Keep in sync.
// Jul 24 2026 — business capped at 10 (was unlimited/null).
const BRAND_CAPS: Record<PlanTier, number | null> = {
  free: 1, starter: 1, pro: 3, business: 10,
};

export default function BrandsPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { brands, activeBrand, loading, switchBrand, removeBrand } = useBrand();
  const { planTier } = useCredits();
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const cap = BRAND_CAPS[planTier];
  const atCap = cap !== null && brands.length >= cap;

  const handleAdd = () => {
    // Launch the guided wizard. Brand creation + activation happen there
    // so the user lands on a populated brand with 20 baked creatives.
    navigate('/brands/new');
  };

  const handleActivate = async (brandId: string) => {
    setBusyId(brandId);
    try {
      // Hard reloads to /brands (stays on this page) so the user can see
      // the new active state. switchBrand handles the reload itself.
      await switchBrand(brandId, '/brands');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch brand.');
      setBusyId(null);
    }
  };

  const handleEdit = async (brandId: string) => {
    setBusyId(brandId);
    try {
      // switchBrand reloads into /brand even when it's already active —
      // single code path, no isActive special-case.
      await switchBrand(brandId, '/brand');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch brand.');
      setBusyId(null);
    }
  };

  const handleDelete = async (brandId: string, name: string) => {
    const ok = await confirm({
      title: `Delete "${name}"?`,
      message: (
        <>
          <p>This brand and all of its products, competitors, creatives, scheduled
          posts, and saved analyses will be archived (soft delete — recoverable
          from the database for now).</p>
          <p className="mt-2 font-bold">You can't undo this from the app yet.</p>
        </>
      ),
      confirmLabel: 'Delete brand',
      destructive: true,
    });
    if (!ok) return;
    setBusyId(brandId);
    try {
      // removeBrand hard-reloads — server may have re-activated another
      // brand, so we let the reload pick up the new state.
      await removeBrand(brandId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete brand.');
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      {/* ---- Minimal top chrome: logo + back-to-app link ---- */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-surface-dark/30 bg-white">
        <a href="/" className="flex items-center">
          <Logo height={56} />
        </a>
        <div className="flex items-center gap-4">
          {/* "Back to app" only makes sense once a brand exists — with zero
              brands /create just bounces back here, so hide it until then. */}
          {brands.length > 0 && (
            <button
              onClick={() => navigate('/create')}
              className="text-sm font-bold text-dark-muted hover:text-dark transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-arrow-left-line" />
              Back to app
            </button>
          )}
          {/* Persistent account access — otherwise the only route to the
              profile is via the logo → home → My Account. */}
          <button
            onClick={() => navigate('/auth/profile')}
            className="px-4 py-2 rounded-full border border-surface-dark/50 text-sm font-bold text-dark hover:border-primary hover:text-primary transition-colors cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-user-line" />
            My Account
          </button>
        </div>
      </header>

      {/* ---- Main content ---- */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-light flex items-center justify-center flex-shrink-0">
                <i className="ri-shapes-line text-primary text-lg" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-semibold text-dark leading-tight">Brands</h1>
                <p className="text-sm text-dark-muted mt-0.5 max-w-md">
                  Each brand has its own products, competitors, creatives, calendar, and analyses.
                  Switching activates a brand and reloads the app into its workspace.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-dark-muted">
                {brands.length}{cap !== null ? ` of ${cap}` : ''} brand{brands.length === 1 ? '' : 's'}
              </span>
              {atCap ? (
                planTier === 'business' ? (
                  // Top tier — no plan to upgrade to. The only way to add
                  // another is to delete an unused brand, so disable the button.
                  <button
                    disabled
                    title={`You've reached the maximum of ${cap} brands. Delete an unused brand to add another.`}
                    className="px-4 py-2 rounded-full bg-surface-dark/40 text-dark-muted text-sm font-bold cursor-not-allowed"
                  >
                    <i className="ri-lock-line mr-1" />
                    Add brand
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/checkout')}
                    className="px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold transition-colors cursor-pointer"
                  >
                    <i className="ri-lock-line mr-1" />
                    Upgrade to add more
                  </button>
                )
              ) : (
                <button
                  onClick={handleAdd}
                  className="px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors cursor-pointer"
                >
                  <i className="ri-add-line mr-1" />
                  Add brand
                </button>
              )}
            </div>
          </header>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200">
              {error}
            </div>
          )}

          {loading && brands.length === 0 ? (
            <div className="text-center py-16 text-dark-muted">Loading brands…</div>
          ) : brands.length === 0 ? (
            <div className="text-center py-16 flex flex-col items-center">
              <div className="w-16 h-16 rounded-2xl bg-primary-light flex items-center justify-center mb-6">
                <i className="ri-shapes-line text-3xl text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-display font-bold text-dark leading-tight">
                Add your website to get started
              </h2>
              <p className="mt-3 text-base text-dark-muted max-w-md leading-relaxed">
                You can start creating ads once you add a brand. Paste your website and
                we&apos;ll pull in your products, logo, and colors automatically.
              </p>
              <button
                onClick={handleAdd}
                className="mt-8 px-8 py-4 rounded-full bg-primary hover:bg-primary-hover text-white text-base font-bold transition-colors cursor-pointer shadow-lg shadow-primary/25 flex items-center gap-2"
              >
                <i className="ri-add-line text-lg" />
                Add a brand to get started
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {brands.map(b => {
                const isBusy = busyId === b.id;
                const isActive = b.isActive;
                const avatar = brandAvatarStyle(b);
                return (
                  <div
                    key={b.id}
                    className={`
                      rounded-2xl border bg-white p-5 transition-all
                      ${isActive
                        ? 'border-primary shadow-md shadow-primary/15'
                        : 'border-surface-dark/40 hover:border-surface-dark/60 hover:shadow-md'}
                    `}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      {/* Monogram tile — brand's own primary color + a
                          black/white contrast letter, identical to the sidebar
                          switcher (brandAvatarStyle). Scraped logos are wide
                          wordmarks that crop badly in a square, so we don't
                          render them here. */}
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-lg leading-none"
                        style={{ backgroundColor: avatar.bg, color: avatar.fg }}
                      >
                        {avatar.letter}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-dark truncate">{b.name}</h3>
                          {isActive && (
                            <span className="inline-block px-2 py-0.5 rounded-full bg-primary-light text-primary text-[10px] font-bold uppercase tracking-wide">
                              Active
                            </span>
                          )}
                        </div>
                        {b.brief && (
                          <p className="text-xs text-dark-muted mt-1 line-clamp-2">{b.brief}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isActive && (
                        <button
                          onClick={() => handleActivate(b.id)}
                          disabled={isBusy}
                          className="px-3 py-1.5 rounded-full bg-primary-light hover:bg-primary hover:text-white text-primary text-xs font-bold transition-colors cursor-pointer disabled:opacity-50"
                        >
                          {isBusy ? 'Switching…' : 'Activate'}
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(b.id)}
                        disabled={isBusy}
                        className="px-3 py-1.5 rounded-full border border-surface-dark/40 hover:border-primary hover:text-primary text-xs font-bold text-dark transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(b.id, b.name)}
                        disabled={isBusy}
                        title="Delete brand"
                        className="ml-auto w-8 h-8 rounded-full hover:bg-red-50 hover:text-red-500 text-dark-muted flex items-center justify-center transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeBrand && (
            <p className="mt-6 text-xs text-dark-muted text-center">
              Currently active: <span className="font-bold text-dark">{activeBrand.name}</span>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
